import { useState, useCallback, useMemo, useRef } from 'react';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { getUserSettings, getOrgConfig, getInitData } from '../utils/api';
import { getOrganizations } from '../utils/organizationApi';
import { bootIntercom } from '../utils/intercom';
import { isEmulating, getEmulatedUser } from '../utils/emulation';
import { clearRequestedOrg, requestedOrgId } from '../utils/deepLink';

const USER_SETTINGS_KEY = 'userSettings';

/**
 * Boot cache (stale-while-revalidate). The last good initData result —
 * settings + accessible orgs — keyed by the signed-in Cognito user, so a
 * reload paints the app straight away from it while initData revalidates in
 * the background (ProtectedRoute). Never used while emulating or when a deep
 * link asks for a specific org; sign-out wipes it with the rest of storage.
 */
const BOOT_CACHE_KEY = 'lodgeic.bootCache.v1';
const BOOT_CACHE_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

const lastAuthUser = () => {
  try {
    const key = Object.keys(localStorage).find((k) => k.startsWith('CognitoIdentityServiceProvider.') && k.endsWith('.LastAuthUser'));
    return key ? localStorage.getItem(key) : null;
  } catch (_) {
    return null;
  }
};

const readBootCache = () => {
  try {
    if (isEmulating() || requestedOrgId()) return null;
    const user = lastAuthUser();
    if (!user) return null;
    const cached = JSON.parse(localStorage.getItem(BOOT_CACHE_KEY) || 'null');
    if (!cached || cached.user !== user || !cached.userSettings?.email || !Array.isArray(cached.organizations)) return null;
    if (Date.now() - (cached.savedAt || 0) > BOOT_CACHE_MAX_AGE_MS) return null;
    const currentOrganization = cached.organizations.find((org) => org.code === localStorage.getItem('currentOrgCode'));
    if (!currentOrganization) return null;
    return { userSettings: cached.userSettings, organizations: cached.organizations, currentOrganization };
  } catch (_) {
    return null;
  }
};

const writeBootCache = (userSettings, organizations) => {
  try {
    const user = lastAuthUser();
    if (!user || isEmulating()) return;
    localStorage.setItem(BOOT_CACHE_KEY, JSON.stringify({ user, userSettings, organizations, savedAt: Date.now() }));
  } catch (_) {
    /* quota / private mode: the next load just fetches */
  }
};

/**
 * Fold a change into the cached settings for writes that bypass
 * updateSettings (e.g. dashboard preference saves), so the next reload's
 * instant paint doesn't bring back the old value.
 */
export const patchBootCacheSettings = (patchFn) => {
  try {
    const cached = JSON.parse(localStorage.getItem(BOOT_CACHE_KEY) || 'null');
    if (!cached?.userSettings) return;
    cached.userSettings = patchFn(cached.userSettings);
    localStorage.setItem(BOOT_CACHE_KEY, JSON.stringify(cached));
  } catch (_) {
    /* the background revalidation corrects it */
  }
};

const clearBootCache = () => {
  try { localStorage.removeItem(BOOT_CACHE_KEY); } catch (_) { /* ignore */ }
};

const sameJson = (a, b) => {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
};

export const useUserSettings = () => {
  const [boot] = useState(readBootCache);
  const [userSettings, setUserSettings] = useState(() => boot?.userSettings ?? null);
  const [isLoading, setIsLoading] = useState(false); // Start as false since we don't auto-fetch
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState(() => boot?.organizations ?? []);
  const [currentOrganization, setCurrentOrganization] = useState(() => boot?.currentOrganization ?? null);
  // True while the state above came from the boot cache and initData has not
  // confirmed it yet (ProtectedRoute revalidates in the background).
  const [isHydratedFromCache, setIsHydratedFromCache] = useState(!!boot);
  // Latest values for the background revalidation's no-op check.
  const latestRef = useRef({ userSettings, organizations, currentOrganization });
  latestRef.current = { userSettings, organizations, currentOrganization };

  const _updateCurrentOrgContext = useCallback((org, allAccessibleOrgs, settings = null, { skipIntercom = false } = {}) => {
    if (!org) {
      console.warn('Attempted to set current organization to null/undefined');
      setCurrentOrganization(null);
      localStorage.removeItem('currentOrgCode');
      localStorage.removeItem('currentOrgId');
      localStorage.removeItem('currentOrgWorkspaceId');
      return;
    }

    const accessibleOrg = allAccessibleOrgs.find(aOrg => aOrg.code === org.code);
    if (!accessibleOrg) {
       console.warn(`Organization with code ${org.code} is not accessible to the current user.`);
       if (allAccessibleOrgs.length > 0) {
          _updateCurrentOrgContext(allAccessibleOrgs[0], allAccessibleOrgs, settings);
       } else {
          setCurrentOrganization(null);
          localStorage.removeItem('currentOrgCode');
          localStorage.removeItem('currentOrgId');
          localStorage.removeItem('currentOrgWorkspaceId');
       }
       return;
    }


    setCurrentOrganization(accessibleOrg);
    localStorage.setItem('currentOrgCode', accessibleOrg.code);
    if (accessibleOrg.id !== undefined && accessibleOrg.id !== null) {
       localStorage.setItem('currentOrgId', accessibleOrg.id);
    } else {
       console.warn('Selected organization object is missing an ID:', accessibleOrg);
       localStorage.removeItem('currentOrgId');
    }
    if (accessibleOrg.workspaceId) {
      localStorage.setItem('currentOrgWorkspaceId', accessibleOrg.workspaceId);
    } else {
       console.warn('Selected organization object is missing workspaceId:', accessibleOrg);
      localStorage.removeItem('currentOrgWorkspaceId');
    }

    // Update Intercom with the user's role for this organization via backend API
    // Get settings from parameter or from localStorage
    const userSettingsData = settings || JSON.parse(localStorage.getItem('userSettings') || '{}');

    // Boot skips this: getIntercomJWT already sent the same role + org.
    if (!skipIntercom && !isEmulating() && accessibleOrg.code && userSettingsData.orgAccess?.[accessibleOrg.code]) {
      const userRole = userSettingsData.orgAccess[accessibleOrg.code].role || 'user';
      const userEmail = userSettingsData.email;
      const userName = userSettingsData.name || userSettingsData.given_name;

      console.log('[Intercom Update] Sending to backend:', {
        organization_role: userRole,
        current_organization: accessibleOrg.code
      });

      // Fire and forget - don't block on this
      import('../utils/api').then(({ updateIntercomAttributes }) => {
        updateIntercomAttributes(userEmail, userEmail, userName, userRole, accessibleOrg.code)
          .then(result => {
            if (result.success) {
              console.log('[Intercom Update] Backend update successful');
            } else {
              console.warn('[Intercom Update] Backend update returned:', result);
            }
          })
          .catch(err => console.error('[Intercom Update] Backend update failed:', err));
      });
    }
  }, []);

  // `background`: revalidate without the loading flag, keep what is on screen
  // when nothing changed (no re-render, no page refetch keyed on these
  // objects), and keep the current state if the request fails.
  const loadAllData = useCallback(async (options) => {
    const background = options?.background === true;
    if (!background) setIsLoading(true);
    setError(null);
    let loadedSettings = null;
    let allOrgsFromApi = [];
    let orgConfig = [];
    let accessibleOrgs = [];

    try {
      // Check if we're in emulation mode
      const emulating = isEmulating();
      const emulatedUser = emulating ? getEmulatedUser() : null;

      // initData authenticates by token, not email, so the Cognito attribute
      // lookup (needed for Intercom) runs alongside it instead of before it.
      const [attributes, initData] = await Promise.all([fetchUserAttributes(), getInitData()]);
      const userEmail = attributes?.email;
      if (!userEmail) throw new Error('User email not found');

      allOrgsFromApi = initData.organizations;
      orgConfig = initData.orgConfig;
      if (emulating && emulatedUser) {
        // When emulating, use the emulated user's settings instead of fetching admin's
        console.log('[useUserSettings] Emulation mode - using emulated user settings:', emulatedUser.email);
        loadedSettings = emulatedUser;
      } else {
        loadedSettings = initData.userSettings;
      }

      if (!loadedSettings) throw new Error('Failed to load user settings');
      
      // Ensure settings structure (isAdmin, orgAccess properties)
      loadedSettings.isAdmin = loadedSettings.isAdmin || 0;
      // Ensure orgAccess exists and log if it doesn't
      if (loadedSettings.orgAccess) {

        Object.keys(loadedSettings.orgAccess).forEach(orgCode => {
           const orgAccessData = loadedSettings.orgAccess[orgCode];
           loadedSettings.orgAccess[orgCode] = {
             reportIds: orgAccessData.reportIds || {},
             dashboardIds: orgAccessData.dashboardIds || [],
             locationIds: orgAccessData.locationIds || [],
             departmentIds: orgAccessData.departmentIds || [-1], // Default to -1 for "all access"
             chartOfAccountDepartmentIds: orgAccessData.chartOfAccountDepartmentIds || [-1], // Default to -1 for "all access"
             dashboards: orgAccessData.dashboards || [],
             reports: orgAccessData.reports || [],
             // PBI report grants mapped to native report groups (server-stamped
             // on load). Array = only these; [] = none; null = unrestricted.
             nativeReports: Array.isArray(orgAccessData.nativeReports) ? orgAccessData.nativeReports : null,
             role: orgAccessData.role || ''
           };
        });
      } else {
        console.warn('[useUserSettings] loadedSettings.orgAccess is missing or null.');
        loadedSettings.orgAccess = {}; // Ensure orgAccess exists
      }
      
      const settingsUnchanged = background && sameJson(loadedSettings, latestRef.current.userSettings);
      if (settingsUnchanged) loadedSettings = latestRef.current.userSettings;
      else setUserSettings(loadedSettings);
      localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(loadedSettings));
      
      // 3. Process organizations: Merge data from all sources for accessible orgs
      if (loadedSettings.orgAccess && allOrgsFromApi.length > 0) {
        const accessibleOrgCodes = Object.keys(loadedSettings.orgAccess);

        // BFF already returns complete organizations with workspaceId and access merged
        // Just filter by accessible org codes - no need to re-merge
        accessibleOrgs = allOrgsFromApi
          .map(org => {

             return org;
          })
          .filter(org => {
            const isAccessible = accessibleOrgCodes.includes(org.code);
            return isAccessible;
          });
        

        if (background && sameJson(accessibleOrgs, latestRef.current.organizations)) {
          accessibleOrgs = latestRef.current.organizations;
        } else {
          setOrganizations(accessibleOrgs); // Set the list of fully formed accessible org objects
        }
      } else {
        console.warn('[useUserSettings] No orgAccess in settings or no orgs fetched from API. Setting organizations to empty.');
        setOrganizations([]); // No accessible orgs
        accessibleOrgs = [];
      }

      // 4. Determine and set the current organization
      let initialOrg = null;
      if (accessibleOrgs.length > 0) {
        const savedOrgCode = localStorage.getItem('currentOrgCode');
        // A deep link (?org=<id>, e.g. from a report email) wins over the
        // last-used org, as long as the user can access it.
        const linkedOrgId = requestedOrgId();
        if (linkedOrgId) {
           initialOrg = accessibleOrgs.find(org => String(org.id) === String(linkedOrgId));
           // Applied or refused, the link has had its say; left in the URL it
           // would switch the user back on every reload.
           clearRequestedOrg();
        }

        if (!initialOrg && savedOrgCode) {
           initialOrg = accessibleOrgs.find(org => org.code === savedOrgCode);

        }
        // If no saved code or saved code org not found/accessible, use the first one
        if (!initialOrg) {

          initialOrg = accessibleOrgs[0];
        }
      }

      // 5. Initialize Intercom with complete user info including role (skip when
      // emulating). Not awaited: the support widget must not hold up first paint.
      if (!emulating) (async () => {
        const initialRole = (initialOrg && loadedSettings.orgAccess?.[initialOrg.code]?.role) || 'user';
        console.log('[Intercom Init] Initializing with role:', {
          email: userEmail,
          orgCode: initialOrg?.code,
          role: initialRole
        });

        try {
          const { getIntercomJWT } = await import('../utils/api');
          // Pass role and org to backend - it will update Intercom via REST API
          const jwtResponse = await getIntercomJWT(
            userEmail,
            userEmail,
            attributes.given_name,
            initialRole,
            initialOrg?.code || ''
          );

          if (!jwtResponse.success || !jwtResponse.jwt) {
            console.warn('[useUserSettings] Failed to get Intercom JWT, initializing without authentication');
          }

          // bootIntercom owns app_id and hide_default_launcher: the messenger has
          // no launcher of its own, because support is an item in the Lodgeic
          // assistant dock. Passing settings inline here is how that flag drifts.
          bootIntercom({
            userId: userEmail,
            name: attributes.given_name,
            email: userEmail,
            jwt: jwtResponse.success ? jwtResponse.jwt : null,
          });
          console.log('[Intercom Init] Successfully initialized');
        } catch (intercomError) {
          console.error('[useUserSettings] Intercom initialization error:', intercomError);
          // Initialize without JWT as fallback
          bootIntercom({
            userId: userEmail,
            name: attributes.given_name,
            email: userEmail,
          });
        }
      })();

      // 6. Update current organization context
      const current = latestRef.current.currentOrganization;
      if (initialOrg && background && current && current.code === initialOrg.code && sameJson(current, initialOrg)) {
        // Revalidation confirmed the org on screen: keep the same object.
      } else if (initialOrg) {
        _updateCurrentOrgContext(initialOrg, accessibleOrgs, loadedSettings, { skipIntercom: !emulating });
      } else {
        console.warn('[useUserSettings] No accessible organizations found. Clearing current organization.');
        // No accessible organizations, clear current org
        _updateCurrentOrgContext(null, []);
      }

      if (!emulating) writeBootCache(loadedSettings, accessibleOrgs);
      setIsHydratedFromCache(false);

    } catch (err) {
      console.error('Error loading user settings or organization data:', err);
      clearBootCache();
      // A failed background refresh keeps the cached session on screen; the
      // next load does a full fetch.
      if (background) return latestRef.current.userSettings;
      // Ensure state is cleared on error
      setError(err.message || 'Failed to load data');
      // Clear potentially inconsistent state on error
      setUserSettings(null);
      setOrganizations([]);
      setCurrentOrganization(null);
      localStorage.removeItem(USER_SETTINGS_KEY);
      localStorage.removeItem('currentOrgCode');
      localStorage.removeItem('currentOrgId');
      localStorage.removeItem('currentOrgWorkspaceId');
    } finally {
      if (!background) setIsLoading(false);
    }
    // Callers that must act on the doc before React re-renders (Login applies
    // the appearance preferences before navigating into the app).
    return loadedSettings;
  }, [_updateCurrentOrgContext]);

  // Don't auto-fetch on mount - this causes race conditions when the user
  // isn't authenticated yet (e.g., on the login page). Instead, we rely on:
  // 1. Login.js calling fetchSettings() after successful authentication
  // 2. ProtectedRoute calling fetchSettings() after verifying auth
  // This prevents the "UserUnAuthenticatedException" error on the login page.

  const setCurrentOrg = useCallback((orgCode) => {
    if (!orgCode) {
      console.warn('setCurrentOrg called with null or empty orgCode');
      return;
    }
    const orgToSet = organizations.find(org => org.code === orgCode);
    if (orgToSet) {
      _updateCurrentOrgContext(orgToSet, organizations);
    } else {
      console.error(`Attempted to switch to non-accessible or non-existent organization code: ${orgCode}`);
    }
  }, [organizations, _updateCurrentOrgContext]);
  
  const updateSettings = useCallback(async (newSettings) => {
    setIsLoading(true);
    setError(null);
    try {
       const updatedSettings = { ...userSettings, ...newSettings };
       updatedSettings.isAdmin = updatedSettings.isAdmin || 0;
       if (updatedSettings.orgAccess) {
          Object.keys(updatedSettings.orgAccess).forEach(orgCode => {
             const orgAccessData = updatedSettings.orgAccess[orgCode];
             updatedSettings.orgAccess[orgCode] = {
               reportIds: orgAccessData.reportIds || {},
               dashboardIds: orgAccessData.dashboardIds || [],
               locationIds: orgAccessData.locationIds || [],
               departmentIds: orgAccessData.departmentIds || [-1], // Default to -1 for "all access"
               chartOfAccountDepartmentIds: orgAccessData.chartOfAccountDepartmentIds || [-1], // Default to -1 for "all access"
               dashboards: orgAccessData.dashboards || [],
               reports: orgAccessData.reports || [],
               // PBI report grants mapped to native report groups (server-stamped
               // on load). Array = only these; [] = none; null = unrestricted.
               nativeReports: Array.isArray(orgAccessData.nativeReports) ? orgAccessData.nativeReports : null,
               role: orgAccessData.role || ''
             };
          });
       }

       setUserSettings(updatedSettings);
       localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(updatedSettings));
       writeBootCache(updatedSettings, latestRef.current.organizations);
       
       if ('orgAccess' in newSettings) {

          await loadAllData();
       }
       
       return updatedSettings;
    } catch (err) {
       console.error('Error updating user settings:', err);
       setError(err.message || 'Failed to update user settings');
       return null;
    } finally {
       setIsLoading(false);
    }
  }, [userSettings, loadAllData]);

  // One object per actual change, so context consumers don't re-render when
  // the provider does for unrelated reasons.
  return useMemo(() => ({
    userSettings,
    organizations,
    currentOrganization,
    isLoading,
    isHydratedFromCache,
    error,
    fetchSettings: loadAllData,
    updateSettings,
    setCurrentOrg
  }), [userSettings, organizations, currentOrganization, isLoading, isHydratedFromCache, error, loadAllData, updateSettings, setCurrentOrg]);
}; 